import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { isUndefined, omitBy } from 'lodash'
import { useForm, useController } from 'react-hook-form'
import { useRecoilState, useRecoilValue } from 'recoil'

import Alert from '@/components/ui/Alert/Alert'
import FormInputNumber from '@/components/ui/FormInputNumber/FormInputNumber'
import FormSwitch from '@/components/ui/FormSwitch/FormSwitch'
import FormTextarea from '@/components/ui/FormTextarea/FormTextarea'
import Button from '@/components/ui/Button/Button'
import { Listbox } from '@headlessui/react'
import ListboxButton from '@/components/ui/Listbox/ListboxButton'
import ListboxOptions from '@/components/ui/Listbox/ListboxOptions'
import ListboxOption from '@/components/ui/Listbox/ListboxOption'
import CardPick from '@/components/ui/CardPick/Card'

import { firstStep, secondStep } from '../../../store/venueRequest'
import { user } from '../../../store/profile'
import { APP_ROUTES } from '../../../utils/constants'
import useOccasionsMap from '../../../hooks/useOccasionsMap'
import useSubmitVenueRequest from '../../../hooks/useSubmitVenueRequest'
import useErrorHandler from '../../../hooks/useErrorHandler'

export interface Props {
  slug: string
  canBeBookedOnlyExclusively?: boolean
}

export const REQUEST_SECOND_STEP = 'requestSecondStep'

const VenueFormSecondStep: React.FC<Props> = ({ slug, canBeBookedOnlyExclusively = false }) => {
  const { t } = useTranslation()
  const TRANSLATION = 'venue.requestForm.secondStep'
  const router = useRouter()
  const errorHandler = useErrorHandler()

  const occasionsMap = useOccasionsMap()

  const userVariable = useRecoilValue(user)
  const firstStepForm = useRecoilValue(firstStep)
  const [formValues, setFormValues] = useRecoilState(secondStep)

  useEffect(() => {
    if (!firstStepForm.dateDeparture) {
      router.push(`${APP_ROUTES.VENUE}/${slug}`)
    }
  }, [firstStepForm])

  const submitRequest = useSubmitVenueRequest(slug, true)

  const [alertError, setAlertError] = useState('')

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    control,
    setError: setFormError,
    clearErrors,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      ...formValues,
      exclusivityRequired: formValues.exclusivityRequired || canBeBookedOnlyExclusively,
    },
  })

  const meetingRoomsNumber = watch('meetingRoomsNumber')

  const { field: meetingRoomsUnknownField } = useController({
    name: 'meetingRoomsUnknown',
    control,
  })

  const { field: cateringRequiredField } = useController({
    name: 'cateringRequired',
    control,
  })

  const { field: activitiesRequiredField } = useController({
    name: 'activitiesRequired',
    control,
  })

  const { field: exclusivityRequiredField } = useController({
    name: 'exclusivityRequired',
    control,
  })

  const { field: eventOccasionField } = useController({
    name: 'eventOccasion',
    control,
    rules: { required: true },
  })

  useEffect(() => {
    const cachedStepString = localStorage?.getItem(REQUEST_SECOND_STEP)

    if (cachedStepString) {
      const cachedStep = JSON.parse(cachedStepString)

      setValue('meetingRoomsNumber', cachedStep.meetingRoomsNumber)
      setValue('comment', cachedStep.comment)
      setValue('meetingRoomsUnknown', cachedStep.meetingRoomsUnknown)
      setValue('cateringRequired', cachedStep.cateringRequired)
      setValue('activitiesRequired', cachedStep.activitiesRequired)
      if (!canBeBookedOnlyExclusively) {
        setValue('exclusivityRequired', cachedStep.exclusivityRequired)
      }
      setValue('eventOccasion', cachedStep.eventOccasion)
    }
  }, [setValue])

  useEffect(() => {
    if (meetingRoomsUnknownField.value) {
      setValue('meetingRoomsNumber', '')
      clearErrors('meetingRoomsNumber')
    } else if (isSubmitted && !meetingRoomsNumber) {
      setFormError('meetingRoomsNumber', {})
    }

    if (meetingRoomsNumber) {
      clearErrors('meetingRoomsNumber')
    }
  }, [
    isSubmitted,
    meetingRoomsUnknownField?.value,
    meetingRoomsNumber,
    setValue,
    clearErrors,
    setFormError,
  ])

  return (
    <form
      onSubmit={handleSubmit(async (data) => {
        const newData = { ...formValues, ...omitBy(data, isUndefined) }
        setFormValues(newData)
        localStorage.setItem(REQUEST_SECOND_STEP, JSON.stringify(newData))

        if (userVariable.loggedIn) {
          try {
            const mergedData = { ...firstStepForm, ...newData }
            const resp = await submitRequest(mergedData)

            if (resp?.data?.status === 'success') {
              router.push({ pathname: `${APP_ROUTES.VENUE_FORM}/${slug}`, query: 'page=4' })
            } else {
              const message = await errorHandler(resp.data)
              setAlertError(message)
            }
          } catch (e) {
            await errorHandler(e, 'Failed to submit venue request')
          }
        } else {
          router.push({ pathname: `${APP_ROUTES.VENUE_FORM}/${slug}`, query: 'page=3' })
        }
      })}
    >
      <h1 className="text-lg font-semibold">{t(`${TRANSLATION}.meetingRoomsHeader`)}</h1>
      <div className="items-left flex flex-col justify-center lg:flex-row lg:items-center">
        <div className="lg:w-3/4">
          <FormInputNumber
            {...register('meetingRoomsNumber', {
              required: !meetingRoomsUnknownField.value,
            })}
            title={t(`${TRANSLATION}.meetingRoomsInput`)}
            min="0"
            disabled={meetingRoomsUnknownField.value}
            error={!!errors.meetingRoomsNumber}
          />
        </div>
        <FormSwitch
          checked={meetingRoomsUnknownField.value}
          onChange={(e) => meetingRoomsUnknownField.onChange(e.target.checked)}
          className="relative top-3 lg:left-5"
        >
          <p className="xs:text-sm mr-5 whitespace-nowrap text-base md:text-lg">
            {t(`${TRANSLATION}.meetingRoomsSwitch`)}
          </p>
        </FormSwitch>
      </div>

      <h2 className="mb-2 mt-10 text-lg font-semibold">{t(`${TRANSLATION}.foodDrinksHeader`)}</h2>
      <div className="items-left flex flex-col justify-center gap-5 xl:flex-row xl:items-center">
        <CardPick
          picked={!cateringRequiredField.value}
          onClick={() => cateringRequiredField.onChange(false)}
          name={t(`${TRANSLATION}.foodDrinks.selfCateringPick`)}
          description={t(`${TRANSLATION}.foodDrinks.selfCateringPickDescription`)}
        />
        <CardPick
          picked={cateringRequiredField.value}
          onClick={() => cateringRequiredField.onChange(true)}
          name={t(`${TRANSLATION}.foodDrinks.venueCateringPick`)}
          description={t(`${TRANSLATION}.foodDrinks.venueCateringPickDescription`)}
        />
      </div>

      <h3 className="mb-2 mt-10 text-lg font-semibold">{t(`${TRANSLATION}.activitiesHeader`)}</h3>
      <div className="items-left flex flex-col justify-center gap-5 xl:flex-row xl:items-center">
        <CardPick
          picked={activitiesRequiredField.value}
          onClick={() => activitiesRequiredField.onChange(true)}
          name={t(`${TRANSLATION}.activities.needActivitiesPick`)}
          description={t(`${TRANSLATION}.activities.needActivitiesPickDescription`)}
        />
        <CardPick
          picked={!activitiesRequiredField.value}
          onClick={() => activitiesRequiredField.onChange(false)}
          name={t(`${TRANSLATION}.activities.noNeedActivitiesPick`)}
          description={t(`${TRANSLATION}.activities.noNeedActivitiesPickDescription`)}
        />
      </div>

      <div className="mb-3 mt-10">
        <p className="text-lg font-semibold">{t(`${TRANSLATION}.exclusivityHeader`)}</p>
        {canBeBookedOnlyExclusively && (
          <span className="text-sm">{t(`${TRANSLATION}.canBeBookedOnlyExclusively`)}</span>
        )}
      </div>
      <div className="flex w-full flex-col gap-4 xl:flex-row xl:gap-2">
        <FormSwitch
          checked={exclusivityRequiredField.value}
          onChange={(e) => exclusivityRequiredField.onChange(e.target.checked)}
          className="relative w-full sm:w-auto"
          disabled={canBeBookedOnlyExclusively}
        >
          <p className="xs:text-sm text-base md:text-lg">{t(`${TRANSLATION}.exclusivitySwitch`)}</p>
        </FormSwitch>
        {!canBeBookedOnlyExclusively && (
          <p className="text-gray-400">{t(`${TRANSLATION}.exclusivityAvailabilitySwitch`)}</p>
        )}
      </div>

      <h1 className="mb-2 mt-9 text-lg font-semibold">{t(`${TRANSLATION}.occasionHeader`)}</h1>
      <p className="mb-2">{t(`${TRANSLATION}.occasionInput`)}</p>
      <Listbox
        onChange={(key) => eventOccasionField.onChange(key)}
        value="selected"
        className="relative"
        as="div"
      >
        {({ open }) => (
          <>
            <ListboxButton open={open} error={!!errors.eventOccasion}>
              {occasionsMap.get(eventOccasionField.value)}
            </ListboxButton>
            <ListboxOptions>
              {Array.from(occasionsMap).map((occasion) => (
                <ListboxOption value={occasion[0]} key={occasion[0]}>
                  {occasion[1]}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </>
        )}
      </Listbox>

      <p className="mb-2 mt-10 text-lg font-semibold">{t(`${TRANSLATION}.elseHeader`)}</p>
      <p className="mb-2">{t(`${TRANSLATION}.elseInput`)}</p>
      <FormTextarea {...register('comment', { required: true })} error={!!errors.comment} />

      <div className="mt-6">
        {alertError && <Alert>{alertError}</Alert>}
        <div className="mt-4 flex items-center justify-center gap-6">
          <Button
            onClick={() => router.push({ pathname: `${APP_ROUTES.VENUE}/${slug}` })}
            variant="tertiary"
            type="button"
          >
            {t('venue.requestForm.backButton')}
          </Button>
          <Button type="submit" variant="secondary" size="xl">
            {userVariable.loggedIn
              ? t('venue.requestForm.thirdStep.submitRequestButton')
              : t('venue.requestForm.nextButton')}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default VenueFormSecondStep
